/**
 * VerificationsPage — revisão de verificações de identidade dos workers.
 * O admin vê foto de perfil, BI (número + foto) e comprovativo de morada,
 * e aprova ou rejeita (com motivo). Segurança Dozy: sem bandidos em casa
 * dos clientes.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { AuthContext } from '../context/AuthContext';
import { getVerifications, approveVerification, rejectVerification, fileUrl } from '../utils/api';
import { ShieldCheck, ShieldX, ShieldAlert, Phone, Mail, MapPin, CreditCard, Briefcase, Camera } from 'lucide-react';
import ScanWorkerDocs from '../components/ScanWorkerDocs';
import '../styles/VerificationsPage.css';

const STATUS_TABS = [
  { id: 'pending',  label: 'Pendentes' },
  { id: 'approved', label: 'Aprovadas' },
  { id: 'rejected', label: 'Rejeitadas' },
  { id: 'all',      label: 'Todas' },
];

const statusBadge = (status) => {
  const map = {
    pending:  ['bg-yellow-500/20 text-yellow-400', 'Pendente'],
    approved: ['bg-green-500/20 text-green-400', 'Aprovada'],
    rejected: ['bg-red-500/20 text-red-400', 'Rejeitada'],
  };
  const [cls, label] = map[status] || map.pending;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
};

const DocImage = ({ src, label }) => (
  <a href={src} target="_blank" rel="noreferrer" className="block group">
    <p className="text-gray-400 text-xs mb-1">{label}</p>
    {src ? (
      <img
        src={src}
        alt={label}
        className="w-full h-36 object-cover rounded-lg border border-gray-700 group-hover:border-teal-500 transition-colors"
      />
    ) : (
      <div className="w-full h-36 rounded-lg border border-dashed border-gray-700 flex items-center justify-center text-gray-600 text-xs">
        Não submetido
      </div>
    )}
  </a>
);

const VerificationsPage = () => {
  // O supervisor consulta os documentos para julgar disputas, mas quem valida
  // é o Operations Manager: é o passo pending → verified do funil dele.
  const { user } = useContext(AuthContext);
  const podeDecidir = user?.role !== 'supervisor';
  const [activeTab, setActiveTab] = useState('pending');
  const { success, error } = useToast();
  const [rejectModal, setRejectModal] = useState({ open: false, verification: null });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanAberto, setScanAberto] = useState(false);

  const { data: verifications, isLoading, execute: refresh } = useApi(
    () => getVerifications(activeTab),
    { immediate: false }
  );

  useEffect(() => { refresh(); }, [activeTab, refresh]);

  const changeTab = (tab) => setActiveTab(tab);

  const handleApprove = async (v) => {
    setBusy(true);
    try {
      await approveVerification(v.verification_id);
      success(`${v.full_name} verificado!`);
      refresh();
    } catch {
      error('Erro ao aprovar verificação');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) { error('Indique o motivo da rejeição'); return; }
    setBusy(true);
    try {
      await rejectVerification(rejectModal.verification.verification_id, reason.trim());
      success('Verificação rejeitada');
      setRejectModal({ open: false, verification: null });
      setReason('');
      refresh();
    } catch {
      error('Erro ao rejeitar verificação');
    } finally {
      setBusy(false);
    }
  };

  const list = verifications || [];

  return (
    <div className="verifications-admin-page space-y-6">
      <div className="verifications-admin-hero"><div><span>Confiança da rede</span><h1>Verificações de identidade</h1><p>Compare os documentos e valide apenas perfis com informação clara e consistente.</p></div><div className="verifications-admin-queue"><ShieldAlert/><span><small>Na vista atual</small><strong>{list.length} registo{list.length === 1 ? '' : 's'}</strong></span></div></div>

      <div className="verification-review-guide"><span><strong>1</strong><small>Confirmar identidade</small></span><i/><span><strong>2</strong><small>Comparar documentos</small></span><i/><span><strong>3</strong><small>Registar decisão</small></span></div>

      {/* Recolha presencial: para trabalhadores sem meios de submeter sozinhos.
          Escondida ao supervisor — ele consulta os documentos para julgar
          disputas, não recolhe nem valida. */}
      <div className="verification-scan-action" hidden={!podeDecidir}>
        <button
          onClick={() => setScanAberto(v => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2e5bff]/15 text-[#00f1fe] border border-[#2e5bff]/40 text-sm font-medium hover:bg-[#2e5bff]/25"
        >
          <Camera className="w-4 h-4" />
          {scanAberto ? 'Fechar digitalização' : 'Digitalizar documentos de um profissional'}
        </button>
      </div>
      {scanAberto && podeDecidir && <ScanWorkerDocs onDone={() => { setScanAberto(false); refresh(); }} />}

      {/* Tabs */}
      <div className="verifications-admin-tabs flex gap-2 flex-wrap">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => changeTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border
              ${activeTab === tab.id
                ? 'bg-teal-500/10 border-teal-500/30 text-teal-400'
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 bg-gray-800 rounded-xl border border-gray-700">
          <ShieldAlert className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Nenhuma verificação {activeTab !== 'all' ? STATUS_TABS.find(t => t.id === activeTab)?.label.toLowerCase() : ''} encontrada.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {list.map((v) => (
            <div key={v.verification_id} className={`verification-review-card verification-review-card--${v.status} bg-gray-800 rounded-xl border border-gray-700 p-5`}>
              {/* Header do cartão */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img
                    src={fileUrl(v.profile_photo)}
                    alt={v.full_name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
                  />
                  <div>
                    <p className="text-white font-semibold">{v.full_name}</p>
                    <p className="text-gray-400 text-xs flex items-center gap-1">
                      <Briefcase className="w-3 h-3" /> {v.profession || 'Sem profissão registada'}
                    </p>
                  </div>
                </div>
                {statusBadge(v.status)}
              </div>

              {/* Dados */}
              <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                <p className="text-gray-300 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-teal-400" /> BI: <span className="text-white font-mono">{v.bi_number}</span>
                </p>
                <p className="text-gray-300 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-teal-400" /> {v.phone_number || 'N/A'}
                </p>
                <p className="text-gray-300 flex items-center gap-2 col-span-2">
                  <Mail className="w-4 h-4 text-teal-400" /> {v.email}
                </p>
                {v.address && (
                  <p className="text-gray-300 flex items-center gap-2 col-span-2">
                    <MapPin className="w-4 h-4 text-teal-400" /> {v.address}
                  </p>
                )}
              </div>

              {/* Documentos */}
              <div className="verification-doc-grid grid grid-cols-3 gap-3 mb-4">
                <DocImage src={fileUrl(v.profile_photo)} label="Foto de Perfil" />
                <DocImage src={fileUrl(v.bi_photo)} label="Bilhete de Identidade" />
                <DocImage src={fileUrl(v.address_proof)} label="Comprov. Morada" />
              </div>

              {v.status === 'rejected' && v.rejection_reason && (
                <p className="text-red-400 text-xs mb-3">Motivo: {v.rejection_reason}</p>
              )}

              {/* Ações */}
              {v.status === 'pending' && (
                <div className="flex gap-3" hidden={!podeDecidir}>
                  <button
                    onClick={() => handleApprove(v)}
                    disabled={busy}
                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-green-500/10
                               text-green-400 hover:bg-green-500/20 border border-green-500/20 transition-colors
                               disabled:opacity-50 text-sm font-medium"
                  >
                    <ShieldCheck className="w-4 h-4" /> Aprovar
                  </button>
                  <button
                    onClick={() => setRejectModal({ open: true, verification: v })}
                    disabled={busy}
                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-red-500/10
                               text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-colors
                               disabled:opacity-50 text-sm font-medium"
                  >
                    <ShieldX className="w-4 h-4" /> Rejeitar
                  </button>
                </div>
              )}

              <p className="text-gray-600 text-xs mt-3">
                Submetido: {v.submitted_at ? new Date(v.submitted_at).toLocaleString('pt-PT') : '-'}
                {v.reviewed_at ? ` • Revisto: ${new Date(v.reviewed_at).toLocaleString('pt-PT')}` : ''}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Modal de rejeição */}
      {rejectModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="verification-reject-modal bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-2">Rejeitar Verificação</h3>
            <p className="text-gray-400 text-sm mb-4">
              {rejectModal.verification?.full_name} — explique o motivo (o profissional vai ver esta mensagem):
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Ex: Foto do BI ilegível. Submeta uma foto mais nítida."
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm
                         focus:outline-none focus:ring-2 focus:ring-teal-500 mb-4"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setRejectModal({ open: false, verification: null }); setReason(''); }}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={busy || !reason.trim()}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors text-sm
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Rejeitar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationsPage;
